import { Preferences } from '@capacitor/preferences'

interface PendingAction {
  id: string
  type: 'create' | 'update' | 'delete' | 'send'
  endpoint: string
  payload: any
  createdAt: number
}

const QUEUE_KEY = 'offline_sync_queue'

export function useOfflineSync() {
  const isSyncing = ref(false)
  const pendingCount = ref(0)

  async function enqueue(action: Omit<PendingAction, 'id' | 'createdAt'>) {
    try {
      const { value } = await Preferences.get({ key: QUEUE_KEY })
      const queue: PendingAction[] = value ? JSON.parse(value) : []
      queue.push({ ...action, id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, createdAt: Date.now() })
      await Preferences.set({ key: QUEUE_KEY, value: JSON.stringify(queue) })
      pendingCount.value = queue.length
    } catch {}
  }

  async function getQueue(): Promise<PendingAction[]> {
    try {
      const { value } = await Preferences.get({ key: QUEUE_KEY })
      return value ? JSON.parse(value) : []
    } catch {
      return []
    }
  }

  async function sync() {
    if (isSyncing.value) return
    if (!navigator.onLine) return

    isSyncing.value = true
    try {
      const api = useApi()
      const queue = await getQueue()
      if (!queue.length) return

      const failed: PendingAction[] = []
      for (const action of queue) {
        try {
          switch (action.type) {
            case 'create':
              await api.post(action.endpoint, action.payload)
              break
            case 'update':
              await api.patch(action.endpoint, action.payload)
              break
            case 'delete':
              await api.delete(action.endpoint)
              break
            case 'send':
              await api.post(action.endpoint, action.payload)
              break
          }
        } catch {
          failed.push(action)
        }
      }

      await Preferences.set({ key: QUEUE_KEY, value: JSON.stringify(failed) })
      pendingCount.value = failed.length
    } finally {
      isSyncing.value = false
    }
  }

  async function clearQueue() {
    await Preferences.remove({ key: QUEUE_KEY })
    pendingCount.value = 0
  }

  async function loadPendingCount() {
    const queue = await getQueue()
    pendingCount.value = queue.length
  }

  window.addEventListener('online', () => sync())

  return { isSyncing, pendingCount, enqueue, sync, getQueue, clearQueue, loadPendingCount }
}
